import {ellipsis} from 'polished';
import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {isDefined} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {InboxModel} from '../../../../../libs/shared/core/src/models/api/inboxModel';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TableCell} from '../../../../../libs/shared/ui/src/components/tables/cells/tableCell';
import {
  TableCellProps,
  TableCellStyleProps,
} from '../../../../../libs/shared/ui/src/components/tables/cells/tableCellTypes';
import {
  getCellBackgroundColor,
  getCellIndexStyles,
} from '../../../../../libs/shared/ui/src/components/tables/helpers/tableCellHelpers';
import {getWidthSettingsStyles} from '../../../../../libs/shared/ui/src/components/tables/tableColumn';
import {ColorDot} from '../../../../../libs/shared/ui/src/components/utility/colorDot';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {legacyInboxColorToPaletteColor} from '../../../../../libs/shared/ui/src/theme/themeColorHelpers';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {OverflowWithCount} from '../../overflow/overflowWithCount';

/*
 * Props.
 */

interface InboxesCellProps extends TableCellProps<ReadonlyArray<InboxModel>> {
  /**
   * If there are several inboxes, render only the first one with an overflow counter.
   * Othgerwise, render as many as possible (fitting the column width) with an overflow counter.
   */
  shouldRenderOneInboxMax?: boolean;
  /** Render the default '--' when the inboxes array is undefined or empty. */
  useDefaultEmptyState?: boolean;
  useInboxIcon?: boolean;
}

/*
 * Consts.
 */

const inboxIconSize = 16;
const dotIconSize = 8;

/*
 * Style.
 */

const StyledInboxesCellContainerDiv = styled.div<TableCellStyleProps>`
  padding: 0 12px;
  display: flex;
  align-items: center;
  overflow: hidden;
  white-space: nowrap;
  height: 100%;

  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)}
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, p.$index)}
  ${(p) =>
    getCellIndexStyles(
      p.$index,
      p.$isSelected,
      p.$shouldRemoveTopBorderRadius,
      p.$shouldRemoveBottomBorderRadius,
    )}
`;

const StyledPaddedDiv = styled.div`
  display: flex;
  flex-flow: row;
  flex-wrap: nowrap;
  max-width: 100%;
  margin-right: 12px;
`;

const StyledIconDiv = styled.div`
  flex: 0 0 ${inboxIconSize}px;
  width: ${inboxIconSize}px;
  margin-right: 8px;
  ${center()};
`;

const selectedLabelStyles = css`
  color: ${(p) => p.theme.greys.white};
`;

const StyledLabelDiv = styled.div<{
  $shouldUseEllipsis?: boolean;
  $isSelected: boolean;
}>`
  overflow: hidden;

  ${(p) => p.$shouldUseEllipsis && ellipsis()};
  ${(p) => (p.$isSelected ? selectedLabelStyles : '')}
`;

const StyledOverflowDiv = styled.div`
  font-weight: ${fontWeights.bold};
  margin-left: 4px;
`;

/*
 * Component.
 */

/**
 * Renders multiple inboxes inside a table cell. Supports the following cases:
 * - Undefined inboxes array: renders "any inbox"
 * - Empty array: renders "no inbox"
 * - Non empty array: renders one or multiple inboxes with overflow
 */
export const InboxesCell: FC<InboxesCellProps> = (props) => {
  const theme = useUserTheme();
  const {
    value,
    isSelected = false,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
    shouldRenderOneInboxMax,
    useDefaultEmptyState,
    useInboxIcon,
  } = props;

  const renderAnyInbox = () =>
    renderUndefinedOrEmptyInboxes(<Trans id="settingstable-inboxes-cell-anyinbox">Any inbox</Trans>);

  const renderNoInbox = () =>
    renderUndefinedOrEmptyInboxes(<Trans id="settingstable-inboxes-cell-noinbox">No inbox</Trans>);

  const renderUndefinedOrEmptyInboxes = (internationalizedLabel: ReactNode) => (
    <>
      <StyledIconDiv>
        <Icon name="inboxSmall" />
      </StyledIconDiv>
      <StyledLabelDiv $isSelected={isSelected}>{internationalizedLabel}</StyledLabelDiv>
    </>
  );

  const makeRenderInbox = (shouldUseEllipsis: boolean) => {
    const renderInbox = (inbox: InboxModel, inboxIndex: number = 0) => {
      const {color: inboxColor, name: inboxName} = inbox;
      const standardizedInboxColor = theme.palette[legacyInboxColorToPaletteColor(inboxColor, theme)].shade40;

      const inboxItem = (
        <>
          <StyledIconDiv>
            {useInboxIcon ? (
              <Icon name="inboxFilled" color={standardizedInboxColor} size={inboxIconSize} />
            ) : (
              <ColorDot
                size={dotIconSize}
                color={standardizedInboxColor}
                hoverColor={standardizedInboxColor}
              />
            )}
          </StyledIconDiv>
          <StyledLabelDiv $shouldUseEllipsis={shouldUseEllipsis} $isSelected={isSelected}>
            {inboxName}
          </StyledLabelDiv>
          {isDefined(value) && inboxIndex < value.length - 1 ? <div>,</div> : null}
        </>
      );

      if (shouldUseEllipsis) {
        return inboxItem;
      }

      return <StyledPaddedDiv>{inboxItem}</StyledPaddedDiv>;
    };

    return renderInbox;
  };

  const renderOneInboxWithOverflowCount = (inboxes: ReadonlyArray<InboxModel>) => {
    if (inboxes.length < 1) {
      return renderNoInbox();
    }

    const firstInbox = inboxes[0];
    const diff = inboxes.length - 1;

    const renderInbox = makeRenderInbox(true);

    return (
      <>
        {renderInbox(firstInbox)}
        {diff ? <StyledOverflowDiv>+{diff}</StyledOverflowDiv> : undefined}
      </>
    );
  };

  const renderInboxesWithOverflowCount = (inboxes: ReadonlyArray<InboxModel>) => {
    if (inboxes.length < 1) {
      return renderNoInbox();
    }

    return (
      <OverflowWithCount<InboxModel>
        elements={inboxes}
        buildElementKey={(inbox) => inbox.id}
        renderElement={makeRenderInbox(false)}
        renderOverflowIndicator={(numberOfHiddenElements) => (
          <StyledOverflowDiv>+{numberOfHiddenElements}</StyledOverflowDiv>
        )}
      />
    );
  };

  const renderEmptyContent = (inboxes?: ReadonlyArray<InboxModel>) => {
    if (inboxes === undefined) {
      return renderAnyInbox();
    }
    return renderNoInbox();
  };

  const renderContent = (inboxes: ReadonlyArray<InboxModel>) => {
    if (!inboxes) {
      return renderAnyInbox();
    }
    if (shouldRenderOneInboxMax) {
      return renderOneInboxWithOverflowCount(inboxes);
    }
    return renderInboxesWithOverflowCount(inboxes);
  };

  return (
    <TableCell<ReadonlyArray<InboxModel>>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      renderEmptyState={
        useDefaultEmptyState
          ? undefined
          : (inboxes) => (
              <StyledInboxesCellContainerDiv
                $settings={settings}
                $isHovering={Boolean(isHovering)}
                $index={index}
                $isSelected={Boolean(isSelected)}
                $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
                $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
              >
                {renderEmptyContent(inboxes)}
              </StyledInboxesCellContainerDiv>
            )
      }
      renderCell={(inboxes) => (
        <StyledInboxesCellContainerDiv
          $settings={settings}
          $isHovering={Boolean(isHovering)}
          $index={index}
          $isSelected={Boolean(isSelected)}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          {renderContent(inboxes)}
        </StyledInboxesCellContainerDiv>
      )}
    />
  );
};
